import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import SamplePanel from './SamplePanel';
import TemplatePanel from './TemplatePanel';
import HistoryPanel from './HistoryPanel';

function Sidebar({ 
  visible, 
  activeTab, 
  onTabChange, 
  savedDiagrams, 
  onLoadDiagram, 
  onDeleteDiagram 
}) {
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const tabs = [
    { id: 'samples', label: 'Samples', icon: '📊' },
    { id: 'templates', label: 'Templates', icon: '📝' },
    { id: 'history', label: 'History', icon: '📋' }
  ];

  // Wrap onLoadDiagram to potentially close sidebar on mobile
  const handleLoadDiagram = (code) => {
    onLoadDiagram(code);
    // Could add logic here to close sidebar on mobile if needed
    // This is handled by the parent component's click outside logic
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'samples':
        return <SamplePanel onLoadDiagram={handleLoadDiagram} />;
      case 'templates':
        return <TemplatePanel onLoadTemplate={handleLoadDiagram} />;
      case 'history':
        return (
          <HistoryPanel
            diagrams={savedDiagrams}
            onLoadDiagram={handleLoadDiagram}
            onDeleteDiagram={onDeleteDiagram}
          />
        );
      default:
        return null;
    }
  };

  return (
    <aside className={`sidebar ${!visible ? 'hidden' : ''}`}>
      <div className="sidebar-header-mobile">
        <h3 style={{ margin: 0, fontSize: 'var(--font-size-lg)', flex: 1 }}>Menu</h3>
        <button 
          className="btn btn--secondary btn--icon sidebar-close-btn"
          onClick={() => {
            // Find and call the toggle function from parent
            const event = new CustomEvent('closeSidebar');
            window.dispatchEvent(event);
          }}
          aria-label="Close sidebar"
        >
          ✕
        </button>
      </div>
      <div className="sidebar-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`sidebar-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => onTabChange(tab.id)}
          >
            <span className="sidebar-tab-icon">{tab.icon}</span>
            <span className="sidebar-tab-label">{tab.label}</span>
          </button>
        ))}
      </div>
      <div className="sidebar-content">
        {renderTabContent()}
      </div>
    </aside>
  );
}

Sidebar.propTypes = {
  visible: PropTypes.bool.isRequired,
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  savedDiagrams: PropTypes.array.isRequired,
  onLoadDiagram: PropTypes.func.isRequired,
  onDeleteDiagram: PropTypes.func.isRequired
};

export default Sidebar;
